import React, { useState, useEffect } from "react";
import { Crown, Calendar, CreditCard, MessagesSquare, Star, CheckCircle2, RefreshCw } from "lucide-react";
import subscriptionRequests from "../../api/Customer/SubscriptionRequests"; // Adjust path

const SubscriptionHeader = ({ currentPlan }) => {
  const [liveUnits, setLiveUnits] = useState(0);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);

  // Function to fetch live subscription data
  const fetchSubscriptionData = async () => {
    try {
      setLoadingUnits(true);
      const response = await subscriptionRequests.getCurrentUserSubscriptionData();
      
      if ((response.status_code === "000" || response.status_code === "200") && response.data) {
        // response.data is the user/subscription record (plan_name, units, subscription_plan)
        setSubscriptionData(response.data);
        const unitsFromAPI =
          response.data.units ??
          response.data.data?.units ??
          response.data.remaining_units ??
          response.data.sms_balance ??
          0;
        setLiveUnits(parseInt(unitsFromAPI, 10) || 0);
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      setLiveUnits(currentPlan?.units || 0);
    } finally {
      setLoadingUnits(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  // Refresh handler
  const handleRefresh = () => {
    fetchSubscriptionData();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const isPostpaid = currentPlan?.name === "Postpaid";

  // Postpaid Plan Header
  if (isPostpaid) {
    return (
      <div className="relative overflow-hidden p-8 mb-10 rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 border-2 border-amber-400/50 shadow-xl transition-all">
        {/* Decorative Background Blurs */}
        <div className="absolute top-0 right-0 w-40 h-40 sm:w-56 sm:h-56 lg:w-72 lg:h-72 bg-white/10 blur-3xl rounded-full translate-x-16 -translate-y-16"></div>
        <div className="absolute bottom-0 left-0 w-28 h-28 sm:w-40 sm:h-40 lg:w-56 lg:h-56 bg-white/10 blur-3xl rounded-full -translate-x-16 translate-y-16"></div>

        <div className="relative z-10">
          {/* Header Section */}
          <div className="flex flex-col gap-4 lg:gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <div className="flex gap-3 items-center mb-4">
                <div className="p-3 rounded-xl backdrop-blur-sm bg-white/20 shadow-lg border border-white/30">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex gap-2 items-center mb-1">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                      Postpaid Plan
                    </h1>
                    <span className="px-3 py-1 text-xs font-semibold text-amber-800 bg-white/90 rounded-full border border-white/50">
                      Custom Enterprise
                    </span>
                  </div>
                  <p className="text-sm sm:text-base lg:text-lg text-white/90">
                    Your personalized business solution with flexible billing
                  </p>
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                {/* Plan Type */}
                <div className="p-4 rounded-2xl border border-white/30 bg-white/20 backdrop-blur-md shadow-sm hover:shadow-md transition">
                  <div className="flex gap-2 items-center mb-2">
                    <Crown className="w-4 h-4 text-yellow-300" />
                    <span className="text-sm font-medium text-white/90">
                      Plan Type
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-white">
                    Postpaid
                  </p>
                </div>

                {/* Billing Type */}
                <div className="p-4 rounded-2xl border border-white/30 bg-white/20 backdrop-blur-md shadow-sm hover:shadow-md transition">
                  <div className="flex gap-2 items-center mb-2">
                    <CreditCard className="w-4 h-4 text-white" />
                    <span className="text-sm font-medium text-white/90">
                      Billing
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-white">
                    Postpaid
                  </p>
                </div>

                {/* SMS Units Left - Live from API */}
                <div className="p-4 rounded-2xl border border-white/30 bg-white/20 backdrop-blur-md shadow-sm hover:shadow-md transition">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex gap-2 items-center">
                      <MessagesSquare className="w-4 h-4 text-white" />
                      <span className="text-sm font-medium text-white/90">
                        Live Units
                      </span>
                    </div>
                    <button
                      onClick={handleRefresh}
                      disabled={loadingUnits}
                      className="p-1 text-white/70 hover:text-white transition-colors disabled:opacity-50"
                      title="Refresh Units"
                    >
                      <RefreshCw className={`w-3 h-3 ${loadingUnits ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                  <p className="text-lg font-semibold text-white">
                    {loadingUnits ? (
                      <span className="inline-block w-16 h-6 bg-white/30 rounded animate-pulse"></span>
                    ) : (
                      `${liveUnits} units`
                    )}
                  </p>
                </div>

                {/* Status */}
                <div className="p-4 rounded-2xl border border-white/30 bg-white/20 backdrop-blur-md shadow-sm hover:shadow-md transition">
                  <div className="flex gap-2 items-center mb-2">
                    <CheckCircle2 className="w-4 h-4 text-green-300" />
                    <span className="text-sm font-medium text-white/90">
                      Status
                    </span>
                  </div>
                  <p className="text-lg font-semibold text-green-300">Active</p>
                </div>
              </div>
            </div>
            
            {/* Premium Badge */}
            <div className="flex justify-center lg:justify-end">
              <div className="p-4 bg-white/20 backdrop-blur-md rounded-2xl border border-white/30 shadow-lg">
                <div className="text-center">
                  <Star className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
                  <p className="text-sm font-semibold text-white">Premium</p>
                  <p className="text-xs text-white/80">Enterprise</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Standard Plan Header
  return (
    <div className="relative overflow-hidden p-8 mb-10 rounded-3xl bg-gradient-to-br from-[#E6EBFF] via-[#F0F3FF] to-[#E1E7FF] border border-[#6C86FF]/40 transition-all">
      {/* Decorative Background Blurs */}
      <div className="absolute top-0 right-0 w-40 h-40 sm:w-56 sm:h-56 lg:w-72 lg:h-72 bg-[#6C86FF]/20 blur-3xl rounded-full translate-x-16 -translate-y-16"></div>
      <div className="absolute bottom-0 left-0 w-28 h-28 sm:w-40 sm:h-40 lg:w-56 lg:h-56 bg-[#6C86FF]/10 blur-3xl rounded-full -translate-x-16 translate-y-16"></div>

      <div className="relative z-10">
        {/* Header Section */}
        <div className="flex flex-col gap-4 lg:gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex-1">
            <div className="flex gap-3 items-center mb-4">
              <div className="p-2 rounded-xl backdrop-blur-sm bg-white/40 shadow-sm">
                <Crown className="w-5 h-5 text-[#6C86FF]" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1A1F4B]">
                  Current Subscription
                </h1>
                <p className="text-sm sm:text-base lg:text-lg text-[#1A1F4B]/70">
                  View details of your current plan and upgrade anytime.
                </p>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              {/* Current Plan */}
              <div className="p-4 rounded-2xl border border-white/30 bg-white/60 backdrop-blur-md shadow-sm hover:shadow-md transition">
                <div className="flex gap-2 items-center mb-2">
                  <Crown className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-medium text-[#1A1F4B]/70">
                    Current Plan
                  </span>
                </div>
                <p className="text-lg font-semibold text-[#1A1F4B]">
                  {currentPlan?.name || "Free Tier"}
                </p>
              </div>

              {/* Cost */}
              <div className="p-4 rounded-2xl border border-white/30 bg-white/60 backdrop-blur-md shadow-sm hover:shadow-md transition">
                <div className="flex gap-2 items-center mb-2">
                  <CreditCard className="w-4 h-4 text-[#6C86FF]" />
                  <span className="text-sm font-medium text-[#1A1F4B]/70">
                    Cost
                  </span>
                </div>
                <p className="text-lg font-semibold text-[#1A1F4B]">
                  {formatCurrency(currentPlan?.price || 0)}
                </p>
              </div>

              {/* SMS Units Left - Live from API */}
              <div className="p-4 rounded-2xl border border-white/30 bg-white/60 backdrop-blur-md shadow-sm hover:shadow-md transition">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex gap-2 items-center">
                    <MessagesSquare className="w-4 h-4 text-[#6C86FF]" />
                    <span className="text-sm font-medium text-[#1A1F4B]/70">
                      Live SMS Units
                    </span>
                  </div>
                  <button
                    onClick={handleRefresh}
                    disabled={loadingUnits}
                    className="p-1 text-[#6C86FF]/70 hover:text-[#6C86FF] transition-colors disabled:opacity-50"
                    title="Refresh Units"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingUnits ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <p className="text-lg font-semibold text-[#1A1F4B]">
                  {loadingUnits ? (
                    <span className="inline-block w-16 h-6 bg-gray-200 rounded animate-pulse"></span>
                  ) : (
                    `${liveUnits} units`
                  )}
                </p>
              </div>

              {/* Status */}
              <div className="p-4 rounded-2xl border border-white/30 bg-white/60 backdrop-blur-md shadow-sm hover:shadow-md transition">
                <div className="flex gap-2 items-center mb-2">
                  <Calendar className="w-4 h-4 text-[#6C86FF]" />
                  <span className="text-sm font-medium text-[#1A1F4B]/70">
                    Status
                  </span>
                </div>
                <p className="text-lg font-semibold text-green-600">Active</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionHeader;